import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Radio, Spin, message } from 'antd';
import { updatePlanFeature, type FeatureDetail } from './permissionManageData';

const FEATURE_NO_PATTERN = /^[0-9]{21}$/;

type FeatureFormValues = {
  featureNo?: string;
  featureCode?: string;
  displayName?: string;
  domainCode?: string;
  status?: string;
  apis?: string;
  description?: string;
};

type PermissionFormModalProps = {
  open: boolean;
  loading?: boolean;
  detail?: FeatureDetail | null;
  configHash?: string;
  onClose: () => void;
  onSaved: () => void;
};

export function PermissionFormModal({
  open,
  loading,
  detail,
  configHash,
  onClose,
  onSaved
}: PermissionFormModalProps) {
  const [form] = Form.useForm<FeatureFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const statusOptions = [
    { value: 'ACTIVE', label: '启用' },
    { value: 'DISABLED', label: '停用' }
  ];

  useEffect(() => {
    if (!open) return;
    const apis = (detail?.apis ?? [])
      .map((item) => [item.httpMethod, item.pathPattern, item.resourceCode].filter(Boolean).join(' '))
      .filter(Boolean)
      .join('\n');
    form.setFieldsValue({
      featureNo: detail?.featureNo ?? '',
      featureCode: detail?.featureCode ?? '',
      displayName: detail?.displayName ?? '',
      domainCode: detail?.domainCode ?? '',
      status: detail?.status,
      apis,
      description: detail?.quotaPolicyOptions?.description ?? ''
    });
  }, [open, detail, form]);

  const submit = async () => {
    if (submitting || loading) return;
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    const featureNo = detail?.featureNo?.trim() ?? '';
    const displayName = values.displayName?.trim() ?? '';
    const status = values.status === 'ACTIVE' || values.status === 'DISABLED' ? values.status : '';
    if (!FEATURE_NO_PATTERN.test(featureNo)) {
      message.warning('功能编码须为 21 位数字');
      return;
    }
    if (!displayName || displayName.length > 128 || !status) return;
    if (!configHash) {
      message.warning('缺少配置校验值，请关闭后重新打开');
      return;
    }
    setSubmitting(true);
    try {
      const res = await updatePlanFeature(featureNo, {
        displayName,
        status,
        expectedConfigHash: configHash
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '保存权限失败');
        return;
      }
      message.success(res.message || '已保存');
      onSaved();
      onClose();
    } catch {
      message.error('保存权限失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title='编辑权限'
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={720}
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            取消
          </Button>
          <Button type='primary' className='rounded-lg' loading={submitting} disabled={loading} onClick={submit}>
            保存
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout='horizontal'
          labelCol={{ flex: '110px' }}
          wrapperCol={{ flex: 1 }}
          labelAlign='right'
          colon
          className='pt-2 admin-perm-form'
        >
          <Form.Item name='featureNo' label='功能编码'>
            <Input disabled />
          </Form.Item>
          <Form.Item name='featureCode' label='功能标识'>
            <Input disabled />
          </Form.Item>
          <Form.Item
            name='displayName'
            label='功能名称'
            rules={[
              { required: true, whitespace: true, message: '请输入功能名称' },
              { max: 128, message: '最多 128 字' }
            ]}
          >
            <Input placeholder='请输入功能名称' maxLength={128} allowClear />
          </Form.Item>
          <Form.Item name='apis' label='绑定接口'>
            <Input.TextArea disabled autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>
          <Form.Item
            name='status'
            label='状态'
            className='admin-perm-radio-item'
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Radio.Group options={statusOptions} />
          </Form.Item>
          {/* <Form.Item name='description' label='限制能力'>
            <Input.TextArea disabled autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item> */}
        </Form>
      </Spin>
    </Modal>
  );
}
